import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import React, { ReactNode } from "react";

export type LegendHorizontalAlign = "left" | "center" | "right";
export type LegendLayout = "horizontal" | "vertical";
export type LegendVerticalAlign = "top" | "middle" | "bottom";

type FlexDirection = "column" | "row";

interface WidgetLegendProps {
  children: ReactNode;
  colorGetter?(item: any, index: number): string;
  colors?: Array<string>;
  containerStyle?: Record<string, any>;
  data: Array<any>;
  horizontalAlign?: LegendHorizontalAlign;
  layout?: LegendLayout;
  legendStyle?: Record<string, any>;
  onClick?(item: any, index: number): void;
  show?: boolean;
  tooltip?(item: any, index: number): ReactNode;
  valueGetter?(item: any, index: number): string;
  verticalAlign?: LegendVerticalAlign;
}

export default function WidgetLegend(props: WidgetLegendProps): React.JSX.Element {
  const theme = useTheme();

  const show = props.show ?? true;

  // Contiainer flow
  const flexDirection: FlexDirection = props.verticalAlign == "middle" ? "row" : "column";

  // Widget order in container flow
  let isFirst = true;
  if (!props.verticalAlign
    || props.verticalAlign == "middle" && props.horizontalAlign == "right"
    || props.verticalAlign == "bottom") {
    isFirst = false;
  }

  // // Legend direction
  let layout: FlexDirection = props.layout == "vertical" ? "column" : "row";
  if (props.verticalAlign == "middle" && !props.layout) {
    layout = "column";
  }

  // Convert between natural input and CSS values
  const alignMap = {
    left: "start",
    center: "center",
    right: "end",
    top: "start",
    middle: "center",
    bottom: "end"
  };

  // Handle legend horizontal and vertical alignments based on the legend position
  let legendAlignment: {
    alignSelf?: string,
    height: string,
    justifyContent?: string
    width: string,
  } = {
    height: "fit-content",
    width: "fit-content"
  }
  if (flexDirection === "row") {
    if (layout === "row") {
      legendAlignment.alignSelf = "center";
      if (props.verticalAlign !== "middle") {
        legendAlignment.width = "100%";
      }
    } else /* "vertical" */ {
      legendAlignment.alignSelf = "center";
    }
  } else /* "column" */ {
    if (layout === "row") {
      legendAlignment.justifyContent = props.horizontalAlign ? alignMap[props.horizontalAlign] : "start";
      if (props.verticalAlign !== "middle") {
        legendAlignment.width = "100%";
      }
    } else /* "vertical" */ {
      legendAlignment.alignSelf = props.horizontalAlign ? alignMap[props.horizontalAlign] : "start";
    }
  }

  // Legend item rendering
  const legend = props.data.map((item, index): ReactNode => {
    const color = props.colorGetter?.(item, index) || props.colors?.[index % props.colors.length] || theme.palette.common.black;
    let title = props.valueGetter?.(item, index) ?? "";
    if (!title && typeof item === "string") {
      return item;
    }
    return (
      <Tooltip key={`WidgetLegend_${index}`} followCursor title={props.tooltip?.(item, index) ?? null}>
        <Box onClick={(item) => props.onClick?.(item, index)} sx={{cursor: props.onClick ? "pointer" : ""}}>
          <Stack
            direction="row"
            sx={{
              gap: 0.5,
              alignItems: "center"
            }}>
            <Box
              sx={{
                height: 10,
                width: 12,
                bgcolor: color
              }} />
            <Typography
              color={color}
              sx={{
                maxWidth: 150,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
            >
              {title}
            </Typography>
          </Stack>
        </Box>
      </Tooltip>
    );
  });

  return (
    <Box
      style={props.containerStyle ?? {}}
      sx={{
        display: "flex",
        flexDirection: flexDirection,
        height: "100%",
        width: "100%",
        alignItems: "center"
      }}>
      {show && isFirst &&
        <Box
          style={props.legendStyle ?? {}}
          sx={{
            display: "flex",
            columnGap: 1,
            flex: "0 1 auto",
            flexDirection: layout,
            flexWrap: "wrap",
            paddingLeft: 1,
            paddingRight: 1,
            paddingBottom: 1,
            rowGap: 0,
            ...legendAlignment
          }}>
          {legend}
        </Box>
      }
      <Box
        sx={{
          display: "flex",
          height: "100%",
          width: "100%",
          flexDirection: "column",
          flex: "1 1 auto",
          alignSelf: "center",
          justifyContent: "center",
          overflow: "hidden"
        }}>
        {props.children}
      </Box>
      {show && !isFirst &&
        <Box
          style={props.legendStyle ?? {}}
          sx={{
            display: "flex",
            columnGap: 1,
            flex: "0 1 auto",
            flexDirection: layout,
            flexWrap: "wrap",
            paddingLeft: 1,
            paddingRight: 1,
            paddingBottom: 1,
            rowGap: 0,
            ...legendAlignment
          }}>
          {legend}
        </Box>
      }
    </Box>
  );
}